import type { Manifest } from '../api/authoring'
import { acceptsUpload, fieldsSchema, typeOf } from './manifest'
import { addressesSomething } from './preview'
import { keyAndLabel } from './records'
import { declaredReferences } from './references'
import { applies, isMachinery, orderedProperties, resolve, type Schema } from './schema'
import { arranges } from './composition'

// What one node says to a visitor, read off the manifest.
//
// `ContentPreview` draws this; it lives here because every rule in it is
// a reading of the schema rather than a decision about pixels, and a
// reading is something a test can hold still. Nothing here names a type
// or a member: the words come off the fields schema, the extensions on it
// say which strings are machinery, and the type's `label` declaration
// says which of its words *name* it.

/** One block of what a node says. */
export type Said =
  | { kind: 'html'; html: string; names: boolean }
  | { kind: 'text'; text: string; names: boolean }
  | { kind: 'list'; items: string[] }

/** The longest text read as a block's name rather than as its prose. */
const NAME_LENGTH = 120

/** What a node says, and the components its fields point a visitor at. */
export interface Saying {
  said: Said[]
  /** Component ids held in fields that name one — a link's page. */
  targets: string[]
  /**
   * Whether something was left out for having been said already. A
   * heading repeating the page's title says nothing *new*, which is not
   * the same as saying nothing a client can read.
   */
  repeated: boolean
}

/** The minimum of a node this reads. */
export interface Speaker {
  type: string
  fields: Record<string, unknown>
}


/**
 * The words a node holds, in the order its type declares them.
 *
 * **Which string is prose is a question the schema answers**: a property
 * with `format: "html"`, or a node whose sibling vocabulary stores
 * `html` — Content keeps its format in an enum beside the body. Every
 * other string is set as text, and a number, a boolean and an enum are
 * settings rather than sentences.
 *
 * Left out, each by a declaration rather than by name:
 *
 *   - **a concealed field**, which delivery never serves — the name an
 *     author files a section under;
 *   - **a field that does not apply** under its `x-when`;
 *   - **machinery** — see `isMachinery`;
 *   - **the record's key**, which is its address;
 *   - **anything already said**, by this node or in `omit`. A record
 *     that carries its name as a title, a card title and a menu title
 *     says it once, and a heading repeating the page's title is the
 *     title the reader has already read.
 *
 * A field naming a component (`x-component-role`) is not words either,
 * and is returned as a target so the caller can say where it leads.
 */
export function says(manifest: Manifest | undefined, node: Speaker, omit: string[] = []): Saying {
  const schema = fieldsSchema(manifest, node.type)
  if (!schema) return { said: [], targets: [], repeated: false }

  const declared = declaredFormat(node)
  const { key } = keyAndLabel(manifest, node.type)
  const naming = new Set(typeOf(manifest, node.type)?.label ?? [])
  const seen = new Set(omit.map(normalise).filter(Boolean))
  const said: Said[] = []
  const targets: string[] = []
  let repeated = false

  const fresh = (words: string) => {
    const normal = normalise(words)
    if (normal === '') return false
    if (seen.has(normal)) {
      repeated = true
      return false
    }
    seen.add(normal)
    return true
  }

  for (const property of orderedProperties(schema, schema)) {
    if (key && property.name === key) continue
    const shape = resolve(property.schema, schema)
    if (!shape || shape['x-concealed'] || isMachinery(shape)) continue
    if (!applies(property.schema, node.fields)) continue

    const value = node.fields[property.name]
    const names = naming.has(property.name)

    if (shape['x-component-role']) {
      if (typeof value === 'string' && value.trim() !== '') targets.push(value.trim())
      continue
    }

    if (shape.type === 'array') {
      if (!Array.isArray(value)) continue
      const items = resolve(shape.items, schema)
      if (!items || isMachinery(items)) continue

      // A list of strings is either paragraphs or items, and the item
      // schema's format says which: a prose block's paragraphs are each
      // markup, a check list's items are each a line.
      if (items.type === 'string' && !items.enum) {
        const strings = value.filter((entry): entry is string => typeof entry === 'string').filter(fresh)
        if (items.format === 'html') {
          for (const html of strings) said.push({ kind: 'html', html, names: false })
        } else {
          const lines = strings.map((entry) => entry.trim()).filter((entry) => !addressesSomething(entry))
          if (lines.length > 0) said.push({ kind: 'list', items: lines })
        }
        continue
      }

      // A list of records is a list on the page — a form's questions are
      // the case that found it.
      if (items.type === 'object') {
        const labels = value
          .map((entry) => recordLabel(items, entry, schema))
          .filter((text): text is string => Boolean(text))
        if (labels.length > 0) said.push({ kind: 'list', items: labels })
      }
      continue
    }

    // **An open map of strings is words a pattern will draw.** A pattern
    // placement's whole output is decided at publish, and what the author
    // gave it to say is the one part of that this client holds.
    if (shape.type === 'object' && !shape.properties && shape.additionalProperties !== false) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) continue
      for (const entry of Object.values(value as Record<string, unknown>)) {
        if (typeof entry !== 'string') continue
        const text = entry.trim()
        if (text === '' || addressesSomething(text) || identifier(text) || !fresh(text)) continue
        said.push(text.startsWith('<') ? { kind: 'html', html: text, names: false } : { kind: 'text', text, names: false })
      }
      continue
    }

    if (shape.type !== 'string' || shape.enum) continue
    if (typeof value !== 'string') continue
    const text = value.trim()
    if (text === '' || addressesSomething(text)) continue
    // A lowercase word with no space is a setting a tenant type stores as
    // text — an `emphasis` of `featured` — unless the type says it is
    // the block's name, or the field declares a format: a date is
    // unspaced and lowercase and is something the page says.
    if (!names && !shape.format && identifier(text)) continue

    const format = shape.format === 'html' ? 'html' : shape.format === 'markdown' ? 'markdown' : declared
    const words = format === 'html' ? plain(value) : text
    if (!fresh(words)) continue
    // **A name is short.** Content names itself by its body, so the
    // declaration alone would set a whole article as a heading; what a
    // type is named by reads as a heading only while it is a line.
    const heading = names && words.trim().length <= NAME_LENGTH
    if (format === 'html') said.push({ kind: 'html', html: value, names: heading })
    else said.push({ kind: 'text', text: value, names: heading })
  }

  return { said, targets, repeated }
}

/**
 * The single edges a node's own content continues through.
 *
 * **One target, and that target a block.** A record's body kept as a
 * Content of its own is the case: it is the record's words, held
 * elsewhere. What is excluded is decided by the manifest — a list (that
 * is `listedEdges`), a picture (that is the cover), a pattern's
 * declaration, and a record, which a pattern *uses* rather than shows.
 */
export function bodyEdges<R extends { name: string }>(manifest: Manifest | undefined, node: Speaker & { references: R[] }): R[] {
  const declared = declaredReferences(manifest, node.type)
  return node.references.filter((reference) => {
    const says = declared.find((candidate) => candidate.name === reference.name)
    if (!says || says.multiple || says.accepts.length === 0) return false
    return says.accepts.every((accepted) => {
      const type = typeOf(manifest, accepted)
      if (!type || type.role !== 'block' || type.defines || type.declaresPatterns) return false
      return !acceptsUpload(manifest, accepted) && !arranges(manifest, accepted)
    })
  })
}

/**
 * The pattern a node is laid out by, and the records it is given.
 *
 * Both read off the declaration: the pattern is the edge accepting a type
 * that declares patterns, and the records are the single edges accepting
 * only records.
 */
export function patternEdges<R extends { name: string }>(
  manifest: Manifest | undefined,
  node: Speaker & { references: R[] },
): { pattern: R | undefined; records: R[] } {
  const declared = declaredReferences(manifest, node.type)
  const accepting = (name: string, test: (accepted: string) => boolean) => {
    const says = declared.find((candidate) => candidate.name === name)
    return Boolean(says && !says.multiple && says.accepts.length > 0 && says.accepts.every(test))
  }
  const pattern = node.references.find((reference) =>
    accepting(reference.name, (accepted) => Boolean(typeOf(manifest, accepted)?.declaresPatterns)),
  )
  const records = node.references.filter((reference) =>
    accepting(reference.name, (accepted) => typeOf(manifest, accepted)?.role === 'record'),
  )
  return { pattern, records }
}

/**
 * What one record in a repeated list is called *to a reader*.
 *
 * Ranked rather than ordered: a form field declares `name` before
 * `label`, so the first string is `first_name` where a visitor sees
 * "First Name". **Something written for a machine is unspaced and
 * uncapitalised**, and something written for a person is one or the
 * other. Where every candidate looks like a key, the first is shown,
 * because a list of questions with a gap in it is worse than one with a
 * key in it.
 */
function recordLabel(items: Schema, entry: unknown, root: Schema): string | undefined {
  if (!entry || typeof entry !== 'object') return undefined
  const record = entry as Record<string, unknown>
  const candidates = orderedProperties(items, root)
    .filter((property) => {
      const shape = resolve(property.schema, root)
      return shape?.type === 'string' && !shape.enum && !shape['x-concealed'] && !isMachinery(shape)
    })
    .map((property) => record[property.name])
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
    .map((value) => value.trim())
    .filter((value) => !addressesSomething(value))

  return candidates.find((value) => !identifier(value)) ?? candidates[0]
}

/**
 * Whether a string was written for a machine rather than for a reader.
 *
 * Unspaced *and* without a capital: `first_name` and `featured` are
 * keys; "First Name" has a space and "Surname" has a capital.
 */
export function identifier(value: string): boolean {
  return !/\s/.test(value) && value === value.toLowerCase()
}

/**
 * The format this node's own fields declare, where one does.
 *
 * Markdown is read as text rather than rendered: a half-parser that
 * handled the headings and not the links would disagree with the site
 * about what the content is.
 */
function declaredFormat(node: Speaker): 'html' | undefined {
  for (const value of Object.values(node.fields)) {
    if (value === 'html') return 'html'
  }
  return undefined
}

/** Markup's words, for comparing one saying with another. */
function plain(html: string): string {
  return new DOMParser().parseFromString(html, 'text/html').body.textContent ?? ''
}

function normalise(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase()
}
